import { useEffect, useRef, useCallback } from "react";

interface SwipeNavigationOptions {
  /** Distância mínima em px para considerar um swipe */
  threshold?: number;
  /** Velocidade mínima em px/ms para considerar um swipe rápido */
  velocityThreshold?: number;
  /** Ângulo máximo em graus para considerar movimento horizontal (evita conflito com scroll vertical) */
  maxAngle?: number;
  /** Se o swipe está habilitado */
  enabled?: boolean;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
}

/**
 * Hook para detectar swipe horizontal em um elemento.
 * Retorna um ref que deve ser aplicado ao elemento alvo.
 * Respeita scroll vertical — só ativa se o movimento for predominantemente horizontal.
 */
export function useSwipeNavigation({
  threshold = 60,
  velocityThreshold = 0.3,
  maxAngle = 35,
  enabled = true,
  onSwipeLeft,
  onSwipeRight,
}: SwipeNavigationOptions) {
  const ref = useRef<HTMLElement | null>(null);
  const touchStart = useRef<{ x: number; y: number; time: number } | null>(null);
  const isHorizontalSwipe = useRef<boolean | null>(null);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled) return;
    const touch = e.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    isHorizontalSwipe.current = null;
  }, [enabled]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || !touchStart.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;

    // Determina na primeira movimentação se é horizontal ou vertical
    if (isHorizontalSwipe.current === null && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
      const angle = Math.abs(Math.atan2(dy, dx) * (180 / Math.PI));
      const isHorizontal = angle < maxAngle || angle > (180 - maxAngle);
      isHorizontalSwipe.current = isHorizontal;
    }

    // Previne scroll vertical apenas se for swipe horizontal confirmado
    if (isHorizontalSwipe.current === true) {
      e.preventDefault();
    }
  }, [enabled, maxAngle]);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (!enabled || !touchStart.current || isHorizontalSwipe.current !== true) {
      touchStart.current = null;
      isHorizontalSwipe.current = null;
      return;
    }

    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dt = Date.now() - touchStart.current.time;
    const velocity = Math.abs(dx) / dt;

    const isSignificant = Math.abs(dx) >= threshold || velocity >= velocityThreshold;

    if (isSignificant) {
      if (dx < 0) {
        onSwipeLeft();
      } else {
        onSwipeRight();
      }
    }

    touchStart.current = null;
    isHorizontalSwipe.current = null;
  }, [enabled, threshold, velocityThreshold, onSwipeLeft, onSwipeRight]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return ref;
}
